import type {
  Agent,
  Catalog,
  OrgDescriptor,
  OrgId,
  ProcessDoc,
  ProcessPatch,
  ProcessSummary,
  WizardReply,
} from '@wand/shared';
import type { OrgDataset } from '@wand/shared/dataset/org.js';
import type { Group } from '@wand/shared/dataset/groups.js';
import type { InboxItem } from '@wand/shared/dataset/inbox.js';
import type { SignalMetric } from '@wand/shared/dataset/signals.js';

/** Base URL for the BFF. Empty string => same origin (proxied in dev, nginx in prod). */
const BASE = import.meta.env.VITE_API_BASE ?? '';

type AnySchema = { parse(v: unknown): unknown };
type Contracts = typeof import('@wand/contracts');
let contractsPromise: Promise<Contracts> | null = null;

/**
 * Dev/test-only response validation against @wand/contracts (ADR-0002): a BFF
 * response that drifts from the contract logs loudly at the call site instead
 * of crashing later at some unrelated use-site. Production builds return
 * immediately — the dynamic import sits in a PROD-dead branch, so zod and the
 * schemas never enter the production bundle.
 */
async function devValidate<T>(value: T, pick: (c: Contracts) => AnySchema, what: string): Promise<T> {
  if (import.meta.env.PROD) return value;
  contractsPromise ??= import('@wand/contracts');
  const contracts = await contractsPromise;
  try {
    pick(contracts).parse(value);
  } catch (err) {
    console.error(`[contracts] ${what} response does not match its schema`, err, value);
  }
  return value;
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Like `http`, but a 404 resolves to `undefined` (lookups that may miss). */
async function httpMaybe<T>(path: string, init?: RequestInit): Promise<T | undefined> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 404) return undefined;
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return (await res.json()) as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

export const api = {
  catalog: () => http<Catalog>('/api/catalog'),

  /* ---- org registry + per-org data ---- */
  listOrgs: () =>
    http<OrgDescriptor[]>('/api/orgs').then((v) =>
      devValidate(v, (c) => c.orgDescriptorSchema.array(), 'listOrgs'),
    ),
  getDataset: (org: OrgId) => http<OrgDataset>(`/api/orgs/${org}/dataset`),

  createAgent: (org: OrgId, input: Omit<Agent, 'pk'>) =>
    http<Agent>(`/api/orgs/${org}/agents`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.agentSchema, 'createAgent'),
    ),
  patchAgent: (org: OrgId, pk: string, patch: Partial<Agent>) =>
    http<Agent>(`/api/orgs/${org}/agents/${pk}`, json('PATCH', patch)).then((v) =>
      devValidate(v, (c) => c.agentSchema, 'patchAgent'),
    ),

  createGroup: (org: OrgId, input: Omit<Group, 'pk'>) =>
    http<Group>(`/api/orgs/${org}/groups`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.groupSchema, 'createGroup'),
    ),
  patchGroup: (org: OrgId, pk: string, patch: Partial<Omit<Group, 'pk'>>) =>
    http<Group>(`/api/orgs/${org}/groups/${pk}`, json('PATCH', patch)).then((v) =>
      devValidate(v, (c) => c.groupSchema, 'patchGroup'),
    ),

  /* ---- inbox actions (ADR-0009) ---- */
  resolveInboxItem: (org: OrgId, id: string, actionId: string) =>
    http<InboxItem>(`/api/orgs/${org}/inbox/${id}/resolve`, json('POST', { actionId })).then((v) =>
      devValidate(v, (c) => c.inboxItemSchema, 'resolveInboxItem'),
    ),
  replyInboxItem: (org: OrgId, id: string, text: string) =>
    http<InboxItem>(`/api/orgs/${org}/inbox/${id}/reply`, json('POST', { text })).then((v) =>
      devValidate(v, (c) => c.inboxItemSchema, 'replyInboxItem'),
    ),
  askInboxItem: (org: OrgId, id: string, text: string) =>
    http<InboxItem>(`/api/orgs/${org}/inbox/${id}/ask`, json('POST', { text })).then((v) =>
      devValidate(v, (c) => c.inboxItemSchema, 'askInboxItem'),
    ),

  createMetric: (org: OrgId, input: Omit<SignalMetric, 'id'> & { id?: string }) =>
    http<SignalMetric>(`/api/orgs/${org}/metrics`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.signalMetricSchema, 'createMetric'),
    ),

  /* ---- editable process documents (the canvas) ---- */
  listProcesses: (org: OrgId) =>
    http<ProcessSummary[]>(`/api/orgs/${org}/process-docs`).then((v) =>
      devValidate(v, (c) => c.processSummarySchema.array(), 'listProcesses'),
    ),
  getProcess: (org: OrgId, id: string) => httpMaybe<ProcessDoc>(`/api/orgs/${org}/process-docs/${id}`),
  createProcess: (org: OrgId, name?: string) =>
    http<ProcessDoc>(`/api/orgs/${org}/process-docs`, json('POST', { name })).then((v) =>
      devValidate(v, (c) => c.processDocSchema, 'createProcess'),
    ),
  saveProcess: (org: OrgId, id: string, patch: ProcessPatch) =>
    http<ProcessDoc>(`/api/orgs/${org}/process-docs/${id}`, json('PUT', patch)).then((v) =>
      devValidate(v, (c) => c.processDocSchema, 'saveProcess'),
    ),

  wizard: (message: string, processId?: string) =>
    http<WizardReply>('/api/wizard', json('POST', { message, processId })),
};
